import { FetchResult, MutationFunctionOptions } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { concat, filter, find, isNil, map, mapObjIndexed, propEq, reduce } from 'ramda';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';

import RoundedCheckbox from '@ui/forms/RoundedCheckbox';

import fullWidth from '@ui/styled/FullWidth';

import { IInterest, IMemberInterest } from '@hooks/useMemberInfo';

import { commonLabels } from '@utils/button';
import { doesNotExist } from '@utils/general';
import { checkbox } from '@utils/validation';
import { convertKeysToSchema } from '@utils/zod';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

export interface IInterestCaptureStepPageContent {
	pageHeader: string;
	pageInfo: string;
	memberInterestContent: Array<{ key: string; value: string }>;
}

interface IInterestCaptureStepProps extends IFlowStepComponentProps {
	pageContent: IInterestCaptureStepPageContent;
	interests: string[];
	updateMemberInterest?: (options?: MutationFunctionOptions) => Promise<FetchResult<string>>;
	interestData?: IInterest[];
	memberInterests?: IMemberInterest[];
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthForm = fullWidth('form');

const InterestCaptureStep: FC<IInterestCaptureStepProps> = ({
	pageContent,
	handleNavNext = () => {},
	interestData = [],
	interests,
	memberInterests = [],
	stepKey,
	updateMemberInterest = () => {},
}) => {
	const { pageHeader, pageInfo, memberInterestContent } = pageContent;

	const convertedSchema = convertKeysToSchema(interests, checkbox);
	const interestSchema = z.object(convertedSchema);
	type Schema = Partial<z.input<typeof interestSchema>>;

	const form = useForm<Schema>({
		defaultValues: reduce(
			(acc, { name, value }) => {
				return { ...acc, [name]: value };
			},
			{},
			memberInterests
		),
		resolver: zodResolver(interestSchema),
		mode: 'onChange',
	});

	const { handleSubmit, register, setValue, watch, getValues } = form;
	watch(interests);

	const onSubmit = async (data: Record<string, boolean>) => {
		let selectedInterests: Array<{ id: string; value: boolean }> = [];
		mapObjIndexed((value: boolean, key) => {
			const selectedInterest = find(propEq('name', key), interestData);
			if (isNil(selectedInterest?.id)) {
				return;
			}

			selectedInterests = concat([{ id: selectedInterest.id, value }], selectedInterests);
		})(filter((interest) => !!interest, data));

		await updateMemberInterest({
			variables: {
				interests: selectedInterests,
			},
		});
		handleNavNext(stepKey);
	};

	const InterestCheckboxes = () =>
		map((interest: string) => {
			const memberInterest = find(propEq('key', interest), memberInterestContent);
			const interestValue = !!getValues(memberInterest?.key ?? '');

			if (doesNotExist(memberInterest?.key)) {
				return <></>;
			}

			return (
				<RoundedCheckbox
					{...register(memberInterest?.key ?? '')}
					checked={!!interestValue}
					onClick={() => setValue(memberInterest?.key ?? '', !interestValue)}
					key={memberInterest?.key}
					label={memberInterest?.value ?? ''}
					labelPosition="left"
				/>
			);
		}, interests);

	return (
		<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
			<FullWidthColumn gap="lg">
				<FullWidthColumn gap="sm">
					<HeadingText>{pageHeader}</HeadingText>
					<BasicText>{pageInfo}</BasicText>
				</FullWidthColumn>
				<FullWidthColumn gap="sm">
					<InterestCheckboxes />
				</FullWidthColumn>
				<NavigationSection>
					<FullWidthButton variant="secondary" label={commonLabels.next} type="submit" />
				</NavigationSection>
			</FullWidthColumn>
		</FullWidthForm>
	);
};

export default InterestCaptureStep;
